/**
 * @file UtilityComponents.js
 * @module src/js/components/UtilityComponents
 * @description
 * **A collection of reusable utility components and functions.**
 * - `toKebabCase`: Converts a string to kebab-case.
 * - `StyledImg`: Renders an image with default fallback and styling, and a fullscreen icon for clickability.
 * - `MailTo`: Creates a mailto link with optional parameters.
 * - `NewTab`: Opens links in a new tab or the current page with optional authentication check.
 * - `Tel`: Creates a telephone link.
 * - `TextWithBr`: Renders text with line breaks.
 * - `ExtendedMarkdown`: Renders markdown content with extended features.
 * - `DownloadLink`: Creates a download link with optional authentication check.
 * 
 * @version
 * <p>
 *  2024-06-15: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-11-30: Added `toKebabCase` function. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-01: Markdown entry point changed to `markdown-to-jsx/react`. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Add default values `src = null` and `alt = ''` to `StyledImg`. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Add `DownloadLink` and `ExternalLink` icons at both inside and outside the link elements. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-03: Add `InputInvalidMessage` component for displaying validation messages. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-03: Click on `StyledImg` to set modal image if `setModalImage` is provided. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-03: Added `Fullscreen` icon to `StyledImg` for indicating clickability. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-03: Migrate `NewTab` to use `useInitTokenTest` hook for token validation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-03: Added `LoadingThreeDots` component for loading indication. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2026-07-12: `ExtendedMarkdown` normalizes CRLF to LF so lists parse correctly. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2026-07-15: Replace `iconLocation` prop with `iconInside` and `iconOutside`. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2026-07-15: Add `newTab` prop to `NewTab` and `DownloadLink` to control the link behavior. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useModal } from "../contexts/ModalProvider";
import defaultImg from "../../images/no-image.svg";
import Markdown from "markdown-to-jsx/react";
import useInitTokenTest from "../hooks/useInitTokenTest";
import { Download, ExternalLink, Fullscreen } from "lucide-react";

// AbC dEf GHI jkl -> abc-def-ghi-jkl
export function toKebabCase(str) {
    return str.toLowerCase().replace(/\s+/g, '-');
}

export function StyledImg({
    src = null,
    alt = "",
    preview = false,
    style = null,
}) {
    const { setModalImage } = useModal();
    const [hovered, setHovered] = useState(false);

    const hasImage = !!src;
    const effectiveSrc = hasImage ? src : defaultImg;

    return (
        <div
            className="image"
            style={{
                display: "flex",
                justifyContent: "center",
                position: "relative",
            }}
        >
            <img
                src={effectiveSrc}
                alt={alt}
                style={{
                    ...style,
                    cursor: hasImage ? "pointer" : "default",
                    opacity: hasImage && hovered ? 0.8 : 1,
                    transition: preview ? "none" : "opacity 0.2s ease-in-out",
                }}
                onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = defaultImg;
                }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={hasImage ? () => setModalImage(src) : undefined}
            />
            {hasImage && (
                <Fullscreen
                    size={preview ? "1.2em" : "1.5em"}
                    style={{
                        position: "absolute",
                        top: "4px",
                        right: "4px",
                        color: "gray",
                        pointerEvents: "none",
                        transition: "all 0.2s ease-in-out",
                        transform: hovered ? "scale(1.2)" : "scale(1)",
                        opacity: hovered ? 1 : 0.7,
                    }}
                />
            )}
        </div>
    );
}

export function MailTo({
    className = null,
    mail,
    subject,
    body,
    cc,
    bcc,
    children,              // <-- allow wrapping CircleButton
    style = null
}) {
    const params = new URLSearchParams();
    if (subject) params.set("subject", subject);
    if (body)    params.set("body", body);
    if (cc)      params.set("cc", Array.isArray(cc) ? cc.join(",") : cc);
    if (bcc)     params.set("bcc", Array.isArray(bcc) ? bcc.join(",") : bcc);

    const to = mail.replace("{at}", "@");
    const encodedParams = params.toString().replace(/\+/g, '%20');
    const href = `mailto:${to}${encodedParams ? `?${encodedParams}` : ""}`;

    return (
        <a className={className} href={href} style={style}>
            {children ? children : to}
        </a>
    );
}

export function NewTab({
    href,
    className = null,
    onClick, onMouseEnter, onMouseLeave,
    spanStyle = { display: 'inline-flex', alignItems: 'center' },
    anchorStyle = null,
    requireAuth = false,
    newTab = true,
    iconLocation = 'outside',
    iconColor = 'currentColor',
    children
}) {
    const { pathname } = useLocation();
    const initTokenTest = useInitTokenTest();

    if (iconLocation !== 'inside' && iconLocation !== 'outside' && iconLocation !== 'none') {
        console.error("NewTab: Please set 'iconLocation' to one of 'inside', 'outside', or 'none'.");
        return null;
    }

    return (
        <span style={spanStyle}>
            <a 
                href={href} 
                className={className} 
                target={newTab ? "_blank" : "_self"} 
                rel="noopener noreferrer"
                onClick={async (e) => {
                    e.preventDefault();
                
                    if (requireAuth) {
                        const popup = newTab ? window.open('', '_blank') : null;
                        if (newTab && !popup) {
                            alert("Please allow pop-ups for this site.");
                            return;
                        }
                
                        const isTokenValid = await initTokenTest({ from: pathname });
                
                        if (isTokenValid?.success) {
                            if (newTab) {
                                popup.location.href = href;
                            } else {
                                window.location.href = href;
                            }
                        } else {
                            popup?.close();
                        }
                    } else {
                        if (newTab) {
                            window.open(href, '_blank', 'noopener,noreferrer');
                        } else {
                            window.location.href = href;
                        }
                    }
                
                    onClick?.(e);
                }}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                style={anchorStyle}
            >
                {children} {iconLocation === 'inside' && (
                    <ExternalLink size='1em' color={iconColor} style={{ verticalAlign: 'middle' }}/>
                )}
            </a>&nbsp;
            {iconLocation === 'outside' && <ExternalLink size='1em' color={iconColor}/>}
        </span>
    );
}

export function Tel({ tel }) {
    return <a href={`tel:${tel}`}>{tel}</a>;
}

export function TextWithBr({ text }) {
    if (text) {
        // match <br>, <br/>, <br />, <BR>, <BR/>, <BR />
        return text.split(/<br\s*\/?>/i).map((line, i) => (
            <span key={i}>
                {line}
                <br />
            </span>
        ));
    }
}

export function ExtendedMarkdown({ children }) {
    // Normalize CRLF/CR to LF: markdown-to-jsx only splits on "\n", so a trailing
    // "\r" on each line breaks list parsing (every item but the last renders as
    // plain text). CRLF can sneak in via pasted content or DB seed data.
    const normalized = typeof children === "string"
        ? children.replace(/\r\n?/g, "\n")
        : children;

    return (
        <Markdown
            options={{
                overrides: {
                    a: {
                        component: (props) => ( // open link in a new tab
                            <a href={props.href} target="_blank" rel="noopener noreferrer">
                                {props.children}
                            </a>
                        ),
                    },
                    span: { // custom span component to apply styles (e.g., color)
                        component: (props) => (
                            <span style={props.style}>{props.children}</span>
                        ),
                    },
                },
            }}
        >{normalized}</Markdown>
    );
}

export function DownloadLink({
    file,
    requireAuth = true,
    children,
    newTab = false,
    iconLocation = 'outside'
}) {
    if (iconLocation !== 'inside' && iconLocation !== 'outside' && iconLocation !== 'none') {
        console.error("DownloadLink: Please set 'iconLocation' to one of 'inside', 'outside', or 'none'.");
        return null;
    }

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <NewTab href={file} requireAuth={requireAuth} iconLocation='none' newTab={newTab}>
                {children} {iconLocation === 'inside' && <Download size='1em' style={{ verticalAlign: 'middle' }}/>}
            </NewTab>
            {iconLocation === 'outside' && <Download size='1em'/>}
        </span>
    );
}

export function InputInvalidMessage({ condition, message = 'Required' }) {
    return condition && (
        <span style={{ fontSize: '0.8em', color: 'lightcoral' }}>
            {message}
        </span>
    );
}

export function LoadingThreeDots() {
    return (
        <div
            className="dots"
            style={{
                display: 'flex',
                justifyContent: 'center',
                margin: '20px 0',
            }}
        >
            <span></span><span></span><span></span>
        </div>
    );
}